"use client";

import React, { useEffect } from "react";

import { Logo } from "@calcom/ui/components/logo";

import Loader from "@components/Loader";

interface Props {
  footerText?: React.ReactNode | string;
  showLogo?: boolean;
  heading?: string;
  subheading?: string;
  loading?: boolean;
  visualVariant?: "login" | "signup" | "forgot" | "reset" | "verify";
  backButton?: React.ReactNode;
}

const STATS = [
  { value: "2.4M", label: "Meetings Booked", color: "#6366f1", showLogos: false },
  { value: "98.7%", label: "Show-up Rate", color: "#10b981", showLogos: false },
  { value: "340K", label: "Hours Saved", color: "#3b82f6", showLogos: false },
  { value: "$20M+", label: "Payments Processed", color: "#f59e0b", showLogos: false },
  { value: "4.8/5", label: "User Rating", color: "#ec4899", showLogos: true },
];

function ReviewLogos() {
  return (
    <div className="auth-review-logos">
      <img src="/capterra-g2.png" alt="Capterra and G2 Crowd ratings" className="auth-review-img" />
    </div>
  );
}

function SignupContent() {
  return (
    <div className="auth-signup-content">
      <div className="auth-animate-in auth-delay-2" style={{ width: "100%", maxWidth: 380 }}>
        <div className="auth-testimonial-card">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <img src="/rachel-chen.png" alt="Rachel Chen" className="auth-testimonial-photo" />
            <div>
              <div className="auth-testimonial-stars">★★★★★</div>
              <p className="auth-testimonial-text">
                "We booked 3x more meetings after switching to Schedly. Clients love how fast and easy it is to schedule. It's a game-changer for our sales team."
              </p>
              <div className="auth-testimonial-author">Rachel Chen</div>
              <div className="auth-testimonial-role">Head of Sales, Meridian Partners</div>
            </div>
          </div>
        </div>
      </div>

      <div className="auth-animate-in auth-delay-3" style={{ width: "100%", maxWidth: 380, marginTop: 34 }}>
        <div className="auth-trusted-logos">
          <img src="/trusted-logos.png" alt="EA, SMART Technologies, SEGA, GoDaddy, Alberta Government, Disney, LeapFrog, flydubai" className="auth-trusted-logos-img" />
        </div>
      </div>
    </div>
  );
}

function VisualPanel({ variant }: { variant: string }) {
  const configs: Record<string, { badge: string; title: string; titleAccent: string; subtitleLine1: string; subtitleLine2: string }> = {
    login: {
      badge: "Scheduling made simple",
      title: "Welcome Back.",
      titleAccent: "",
      subtitleLine1: "Your calendar is waiting.",
      subtitleLine2: "Pick up right where you left off.",
    },
    signup: {
      badge: "Free forever plan available",
      title: "Start Scheduling",
      titleAccent: "In Minutes.",
      subtitleLine1: "Create booking pages and grow your business.",
      subtitleLine2: "",
    },
    forgot: {
      badge: "Account recovery",
      title: "Reset Your",
      titleAccent: "Password.",
      subtitleLine1: "No worries — we've got you.",
      subtitleLine2: "We'll send you a secure link to get back in.",
    },
    reset: {
      badge: "Almost there",
      title: "Set Your New",
      titleAccent: "Password.",
      subtitleLine1: "Choose a strong password",
      subtitleLine2: "to keep your account secure.",
    },
    verify: {
      badge: "One last step",
      title: "Verify Your",
      titleAccent: "Email.",
      subtitleLine1: "Check your inbox for",
      subtitleLine2: "a verification link to activate your account.",
    },
  };

  const config = configs[variant] || configs.login;
  const isSignup = variant === "signup";

  return (
    <div className="auth-visual" aria-hidden="true">
      <div className="auth-grid-bg" />
      <div className="auth-aurora auth-aurora-1" />
      <div className="auth-aurora auth-aurora-2" />
      <div className="auth-aurora auth-aurora-3" />

      {[...Array(8)].map((_, i) => (
        <div key={i} className={`auth-orb auth-orb-${i + 1}`} />
      ))}

      <div className="auth-sparkle auth-sparkle-1" />
      <div className="auth-sparkle auth-sparkle-2" />
      <div className="auth-sparkle auth-sparkle-3" />
      <div className="auth-sparkle auth-sparkle-4" />
      <div className="auth-sparkle auth-sparkle-5" />
      <div className="auth-sparkle auth-sparkle-6" />

      <div style={{ position: "relative", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", width: "100%", height: "100%", padding: isSignup ? "32px 32px 24px" : "24px 36px", zIndex: 2, overflowY: isSignup ? "auto" : "hidden" }}>
        <div className="auth-animate-in" style={{ marginBottom: 12 }}>
          <div className="auth-badge-pill">
            <span className="auth-badge-dot" />
            {config.badge}
          </div>
        </div>

        <div className="auth-animate-in auth-delay-1" style={{ textAlign: "center", marginBottom: isSignup ? 28 : 32 }}>
          <h1 className="auth-hero-title">
            {config.titleAccent ? (
              <>{config.title}<br /><span className="auth-gradient-text">{config.titleAccent}</span></>
            ) : (
              <>{config.title.split(" ").slice(0, -1).join(" ")}{" "}<span className="auth-gradient-text">{config.title.split(" ").slice(-1)[0]}</span></>
            )}
          </h1>
          <p className="auth-subtitle">
            {config.subtitleLine1}<br />
            {config.subtitleLine2}
          </p>
        </div>

        {isSignup ? (
          <SignupContent />
        ) : (
          <div className="auth-stat-stage">
            {STATS.map((stat, i) => (
              <div key={i} className={`auth-stat-solo auth-stat-solo-${i}`}>
                <div className="auth-stat-number" style={{ color: stat.color }}>{stat.value}</div>
                <div className="auth-stat-name">{stat.label}</div>
                {stat.showLogos && <ReviewLogos />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const STAT_COUNT = 5;
const HOLD = 2.8;
const TRANS = 0.7;
const CYCLE = HOLD + TRANS;
const TOTAL = CYCLE * STAT_COUNT;

function p(s: number) { return ((s / TOTAL) * 100).toFixed(2); }

const directions = [
  { enter: "translate(-120px, 0) scale(0.6) rotate(-12deg)", exit: "translate(120px, 0) scale(0.6) rotate(12deg)" },
  { enter: "translate(0, 100px) scale(0.6) rotate(8deg)", exit: "translate(0, -100px) scale(0.6) rotate(-8deg)" },
  { enter: "translate(120px, 0) scale(0.6) rotate(12deg)", exit: "translate(-120px, 0) scale(0.6) rotate(-12deg)" },
  { enter: "translate(0, -100px) scale(0.6) rotate(-8deg)", exit: "translate(0, 100px) scale(0.6) rotate(8deg)" },
  { enter: "translate(-80px, 80px) scale(0.6) rotate(10deg)", exit: "translate(80px, -80px) scale(0.6) rotate(-10deg)" },
];

function buildStatKF(i: number) {
  const s = i * CYCLE;
  const eEnd = s + TRANS;
  const xStart = s + HOLD;
  const xEnd = xStart + TRANS;
  const d = directions[i];

  return `
@keyframes auth-stat-${i} {
  0% { opacity: 0; transform: ${d.enter}; filter: blur(12px); }
  ${p(s)}% { opacity: 0; transform: ${d.enter}; filter: blur(12px); }
  ${p(s + TRANS * 0.25)}% { opacity: 0.7; transform: translate(0,0) scale(1.06) rotate(0deg); filter: blur(1px); }
  ${p(eEnd)}% { opacity: 1; transform: translate(0,0) scale(1) rotate(0deg); filter: blur(0); }
  ${p(xStart)}% { opacity: 1; transform: translate(0,0) scale(1) rotate(0deg); filter: blur(0); }
  ${p(xStart + TRANS * 0.4)}% { opacity: 0.7; transform: scale(1.04) rotate(0deg); filter: blur(1px); }
  ${p(xEnd)}% { opacity: 0; transform: ${d.exit}; filter: blur(12px); }
  100% { opacity: 0; transform: ${d.exit}; filter: blur(12px); }
}`;
}

const dynKF = Array.from({ length: STAT_COUNT }, (_, i) => buildStatKF(i)).join("\n");
const dynCls = Array.from({ length: STAT_COUNT }, (_, i) =>
  `.auth-stat-solo-${i} { animation: auth-stat-${i} ${TOTAL}s cubic-bezier(0.22, 1, 0.36, 1) infinite; }`
).join("\n");

const AUTH_STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

html:has(.auth-page-wrapper) {
  color-scheme: light !important;
}

html:has(.auth-page-wrapper),
html:has(.auth-page-wrapper) body {
  background: #ffffff !important;
  color: #0f172a !important;
}

html.dark:has(.auth-page-wrapper) * {
  --tw-bg-opacity: 1;
}

html.dark:has(.auth-page-wrapper) .dark\\:hidden {
  display: block !important;
}

html.dark:has(.auth-page-wrapper) .hidden.dark\\:block {
  display: none !important;
}

html.dark:has(.auth-page-wrapper) .auth-page-wrapper,
html.dark:has(.auth-page-wrapper) .auth-page-wrapper * {
  --cal-bg-default: #ffffff;
  --cal-bg-subtle: #f8fafc;
  --cal-bg-muted: #f1f5f9;
  --cal-bg-inverted: #0f172a;
  --cal-bg-emphasis: #f1f5f9;
  --cal-text-default: #0f172a;
  --cal-text-emphasis: #0f172a;
  --cal-text-subtle: #64748b;
  --cal-text-muted: #94a3b8;
  --cal-text-inverted: #ffffff;
  --cal-border-default: #e2e8f0;
  --cal-border-subtle: #e2e8f0;
  --cal-border-muted: #f1f5f9;
  --cal-border-emphasis: #0f172a;
  color-scheme: light;
}

html.dark .auth-form-side button:not([type="submit"]):not([data-testid="signup-submit-button"]):not([data-testid="saml-submit-button"]):not([data-testid="google"]):not([data-testid="continue-with-google-button"]) {
  background-color: #ffffff !important;
  color: #1e293b !important;
  border-color: #e2e8f0 !important;
}

html.dark .auth-form-side input,
html.dark .auth-form-side textarea,
html.dark .auth-form-side select {
  background-color: #ffffff !important;
  color: #0f172a !important;
  border-color: #e2e8f0 !important;
}

html.dark .auth-form-side label,
html.dark .auth-form-side p,
html.dark .auth-form-side span:not(.auth-badge-dot):not(.auth-gradient-text) {
  color: inherit;
}

.auth-page-wrapper {
  display: flex;
  height: 100vh;
  width: 100%;
  background: #ffffff;
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: antialiased;
  color: #0f172a;
}

.auth-visual {
  display: none;
  width: 50%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(160deg, #f8f7ff 0%, #ede9fe 20%, #e0e7ff 40%, #ddd6fe 60%, #c7d2fe 80%, #f0f9ff 100%);
}

@media (min-width: 1024px) {
  .auth-visual { display: flex; }
}

.auth-form-side {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #ffffff;
  overflow-y: auto;
  height: 100vh;
}

@media (min-width: 1024px) {
  .auth-form-side { padding: 24px 48px; }
}

.auth-form-container {
  width: 100%;
  max-width: 440px;
}

/* ===== BACKGROUND ===== */

.auth-grid-bg {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 1px 1px, rgba(99, 102, 241, 0.06) 1px, transparent 0);
  background-size: 32px 32px;
}

.auth-aurora {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  will-change: transform;
}

.auth-aurora-1 {
  width: 600px; height: 600px;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.35) 0%, rgba(99, 102, 241, 0.15) 50%, transparent 70%);
  top: -200px; right: -150px;
  animation: auth-aurora-drift 12s ease-in-out infinite;
}

.auth-aurora-2 {
  width: 500px; height: 500px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.25) 0%, rgba(99, 102, 241, 0.1) 50%, transparent 70%);
  bottom: -100px; left: -120px;
  animation: auth-aurora-drift 16s ease-in-out infinite reverse;
}

.auth-aurora-3 {
  width: 350px; height: 350px;
  background: radial-gradient(circle, rgba(168, 85, 247, 0.2) 0%, transparent 60%);
  top: 40%; left: 30%;
  animation: auth-aurora-drift 14s ease-in-out infinite 3s;
}

@keyframes auth-aurora-drift {
  0%, 100% { transform: translate(0, 0) scale(1) rotate(0deg); }
  25% { transform: translate(40px, -30px) scale(1.1) rotate(2deg); }
  50% { transform: translate(-20px, 20px) scale(0.95) rotate(-1deg); }
  75% { transform: translate(25px, -10px) scale(1.05) rotate(1deg); }
}

.auth-orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  will-change: transform, opacity;
}

.auth-orb-1 { width: 8px; height: 8px; background: rgba(99, 102, 241, 0.5); top: 12%; left: 18%; animation: auth-orb-float 7s ease-in-out infinite; }
.auth-orb-2 { width: 5px; height: 5px; background: rgba(139, 92, 246, 0.6); top: 28%; right: 12%; animation: auth-orb-float 9s ease-in-out infinite 1s; }
.auth-orb-3 { width: 10px; height: 10px; background: rgba(168, 85, 247, 0.3); bottom: 22%; left: 25%; animation: auth-orb-float 8s ease-in-out infinite 2s; }
.auth-orb-4 { width: 6px; height: 6px; background: rgba(99, 102, 241, 0.4); top: 55%; right: 20%; animation: auth-orb-float 10s ease-in-out infinite 0.5s; }
.auth-orb-5 { width: 4px; height: 4px; background: rgba(139, 92, 246, 0.5); top: 75%; left: 12%; animation: auth-orb-float 6s ease-in-out infinite 3s; }
.auth-orb-6 { width: 7px; height: 7px; background: rgba(59, 130, 246, 0.35); top: 8%; left: 55%; animation: auth-orb-float 11s ease-in-out infinite 1.5s; }
.auth-orb-7 { width: 5px; height: 5px; background: rgba(168, 85, 247, 0.45); bottom: 35%; right: 8%; animation: auth-orb-float 7.5s ease-in-out infinite 2.5s; }
.auth-orb-8 { width: 3px; height: 3px; background: rgba(99, 102, 241, 0.6); top: 42%; left: 8%; animation: auth-orb-float 8.5s ease-in-out infinite 4s; }

@keyframes auth-orb-float {
  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
  20% { transform: translate(15px, -25px) scale(1.4); opacity: 0.7; }
  40% { transform: translate(-10px, -50px) scale(0.8); opacity: 0.5; }
  60% { transform: translate(20px, -35px) scale(1.2); opacity: 0.8; }
  80% { transform: translate(-5px, -15px) scale(1.1); opacity: 0.4; }
}

/* ===== SPARKLES ===== */

.auth-sparkle {
  position: absolute;
  pointer-events: none;
  z-index: 1;
}

.auth-sparkle::before,
.auth-sparkle::after {
  content: '';
  position: absolute;
  background: white;
}

.auth-sparkle::before {
  width: 2px; height: 12px;
  border-radius: 2px;
  box-shadow: 0 0 6px 2px rgba(255,255,255,0.6), 0 0 12px 4px rgba(139,92,246,0.3);
}

.auth-sparkle::after {
  width: 12px; height: 2px;
  border-radius: 2px;
  top: 5px; left: -5px;
  box-shadow: 0 0 6px 2px rgba(255,255,255,0.6), 0 0 12px 4px rgba(139,92,246,0.3);
}

@keyframes auth-sparkle-pop {
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  15% { opacity: 1; transform: scale(1.2) rotate(20deg); }
  30% { opacity: 1; transform: scale(1) rotate(45deg); }
  50% { opacity: 0.6; transform: scale(0.8) rotate(60deg); }
  70% { opacity: 0; transform: scale(0.3) rotate(80deg); }
}

.auth-sparkle-1 { top: 20%; left: 15%; animation: auth-sparkle-pop 3s ease-in-out infinite 0s; }
.auth-sparkle-2 { top: 35%; right: 18%; animation: auth-sparkle-pop 3.5s ease-in-out infinite 0.8s; }
.auth-sparkle-3 { bottom: 25%; left: 22%; animation: auth-sparkle-pop 2.8s ease-in-out infinite 1.6s; }
.auth-sparkle-4 { top: 60%; right: 10%; animation: auth-sparkle-pop 3.2s ease-in-out infinite 2.2s; }
.auth-sparkle-5 { top: 15%; left: 45%; animation: auth-sparkle-pop 4s ease-in-out infinite 0.5s; }
.auth-sparkle-6 { bottom: 15%; right: 30%; animation: auth-sparkle-pop 3.8s ease-in-out infinite 1.2s; }

/* ===== ANIMATIONS ===== */

@keyframes auth-slide-in {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes auth-pulse-soft {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.2); }
}

.auth-animate-in { animation: auth-slide-in 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
.auth-delay-1 { animation-delay: 0.08s; }
.auth-delay-2 { animation-delay: 0.16s; }
.auth-delay-3 { animation-delay: 0.24s; }

/* ===== TEXT STYLES ===== */

.auth-gradient-text {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 40%, #a855f7 80%, #6366f1 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: auth-gradient-shift 6s ease-in-out infinite;
}

@keyframes auth-gradient-shift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.auth-hero-title {
  font-size: 48px;
  font-weight: 900;
  line-height: 1.05;
  color: #0f172a;
  letter-spacing: -0.04em;
  margin-bottom: 14px;
}

.auth-subtitle {
  font-size: 18px;
  color: #64748b;
  line-height: 1.5;
  max-width: 400px;
  margin: 0 auto;
  font-weight: 400;
}

/* ===== BADGE PILL ===== */

.auth-badge-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px;
  background: rgba(255,255,255,0.85);
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  color: #6366f1;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border: 1px solid rgba(99,102,241,0.15);
  backdrop-filter: blur(12px);
  box-shadow: 0 2px 12px rgba(99, 102, 241, 0.08);
}

.auth-badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #a855f7);
  animation: auth-pulse-soft 2s infinite;
}

/* ===== SIGNUP CONTENT ===== */

.auth-signup-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.auth-testimonial-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 18px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.02);
}

.auth-testimonial-photo {
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 50%;
  object-fit: cover;
  margin-top: 2px;
}

.auth-testimonial-stars {
  font-size: 12px;
  color: #f59e0b;
  letter-spacing: 1px;
  margin-bottom: 6px;
}

.auth-testimonial-text {
  font-size: 13px;
  color: #334155;
  line-height: 1.5;
  font-style: italic;
  margin: 0 0 8px 0;
  font-weight: 450;
}

.auth-testimonial-author {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.auth-testimonial-role {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
}

.auth-trusted-label {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  text-align: center;
  margin-bottom: 14px;
}

.auth-trusted-logos {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
}

.auth-form-side button {
  text-transform: capitalize;
}

.auth-form-side .auth-submit-btn .text-subtle,
.auth-form-side button[type="submit"] .text-subtle {
  color: rgba(255, 255, 255, 0.7) !important;
}

.auth-region-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  transition: all 0.15s ease;
  font-family: inherit;
}

.auth-region-btn:hover {
  border-color: #6366f1;
  background: #f8fafc;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.08);
}

.auth-region-flag {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
}

.auth-trusted-logos-img {
  max-width: 100%;
  height: auto;
  opacity: 0.5;
}

.auth-delay-4 { animation-delay: 0.32s; }

/* ===== SOLO STAT STAGE ===== */

.auth-stat-stage {
  position: relative;
  width: 100%;
  max-width: 420px;
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-stat-solo {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  opacity: 0;
  will-change: transform, opacity, filter;
}

.auth-stat-number {
  font-size: 130px;
  font-weight: 900;
  letter-spacing: -0.05em;
  line-height: 1;
  margin-bottom: 10px;
  text-shadow: 0 2px 20px rgba(0,0,0,0.06);
}

.auth-stat-name {
  font-size: 22px;
  color: #475569;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.auth-review-logos {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 18px;
}

.auth-review-img {
  max-width: 320px;
  height: auto;
  opacity: 0.95;
}

/* ===== DYNAMIC KEYFRAMES ===== */

${dynKF}
${dynCls}

/* ===== FORM SIDE OVERRIDES ===== */

.auth-form-side input[type="email"],
.auth-form-side input[type="password"],
.auth-form-side input[type="text"],
.auth-form-side input[type="number"],
.auth-form-side input[type="tel"] {
  height: 44px !important;
  padding: 0 14px !important;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 10px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #0f172a !important;
  background: #ffffff !important;
  outline: none !important;
  font-family: 'Inter', -apple-system, sans-serif !important;
  box-sizing: border-box !important;
  transition: border-color 0.2s, box-shadow 0.2s !important;
  width: 100% !important;
}

.auth-form-side input[type="email"]:focus,
.auth-form-side input[type="password"]:focus,
.auth-form-side input[type="text"]:focus,
.auth-form-side input[type="number"]:focus,
.auth-form-side input[type="tel"]:focus {
  border-color: #6366f1 !important;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1) !important;
}

.auth-form-side input::placeholder {
  color: #94a3b8 !important;
  font-weight: 400 !important;
}

.auth-form-side div.border.group {
  border-radius: 10px !important;
  border: 1.5px solid #e2e8f0 !important;
  overflow: hidden !important;
  background: #ffffff !important;
  box-shadow: none !important;
  min-height: 44px !important;
}

.auth-form-side div.border.group:focus-within {
  border-color: #6366f1 !important;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1) !important;
}

.auth-form-side div.border.group input {
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  height: 100% !important;
  background: transparent !important;
}

.auth-form-side .shadow-outline-gray-rested {
  box-shadow: none !important;
}

.auth-form-side label {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #334155 !important;
  font-family: 'Inter', -apple-system, sans-serif !important;
  letter-spacing: -0.01em !important;
}

.auth-form-side button.w-full,
.auth-form-side button[type="submit"],
.auth-form-side button[data-testid="google"],
.auth-form-side button[data-testid="continue-with-google-button"],
.auth-form-side button[data-testid="continue-with-email-button"],
.auth-form-side button[data-testid="saml-login-button"],
.auth-form-side button[data-testid="samlAndOidc"],
.auth-form-side button[data-testid="signup-submit-button"],
.auth-form-side button[data-testid="saml-submit-button"] {
  height: 44px !important;
  border-radius: 10px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  font-family: 'Inter', -apple-system, sans-serif !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  box-sizing: border-box !important;
}

.auth-form-side button[type="submit"],
.auth-form-side button[data-testid="signup-submit-button"],
.auth-form-side button[data-testid="saml-submit-button"] {
  background: linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #7c3aed 100%) !important;
  color: #ffffff !important;
  border: none !important;
  font-weight: 700 !important;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.25) !important;
}

.auth-form-side button[type="submit"]:hover,
.auth-form-side button[data-testid="signup-submit-button"]:hover,
.auth-form-side button[data-testid="saml-submit-button"]:hover {
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.35) !important;
  transform: translateY(-1px);
}

.auth-form-side button[type="submit"]:disabled,
.auth-form-side button[data-testid="signup-submit-button"]:disabled {
  opacity: 0.6 !important;
  transform: none !important;
  cursor: not-allowed !important;
}

.auth-form-side button[data-testid="google"],
.auth-form-side button[data-testid="continue-with-google-button"] {
  background: linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #7c3aed 100%) !important;
  color: #ffffff !important;
  border: none !important;
  font-weight: 700 !important;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.25) !important;
}

.auth-form-side button[data-testid="google"]:hover,
.auth-form-side button[data-testid="continue-with-google-button"]:hover {
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.35) !important;
  transform: translateY(-1px);
}

.auth-form-side button[data-testid="continue-with-email-button"],
.auth-form-side button[data-testid="saml-login-button"],
.auth-form-side button[data-testid="samlAndOidc"] {
  background: #ffffff !important;
  color: #1e293b !important;
  border: 1.5px solid #e2e8f0 !important;
  font-weight: 600 !important;
  box-shadow: none !important;
}

.auth-form-side button[data-testid="continue-with-email-button"]:hover,
.auth-form-side button[data-testid="saml-login-button"]:hover,
.auth-form-side button[data-testid="samlAndOidc"]:hover {
  border-color: #c7d2fe !important;
  background: #f8f7ff !important;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.08) !important;
}

.auth-form-side button[data-testid="continue-with-saml-button"] {
  background: transparent !important;
  color: #6366f1 !important;
  border: none !important;
  font-weight: 600 !important;
  box-shadow: none !important;
  height: 36px !important;
}

.auth-form-side h1,
.auth-form-side h2 {
  font-size: 24px !important;
  font-weight: 800 !important;
  color: #0f172a !important;
  letter-spacing: -0.03em !important;
  font-family: 'Inter', -apple-system, sans-serif !important;
  line-height: 1.2 !important;
}

.auth-form-side .text-subtle,
.auth-form-side .text-default {
  color: #64748b !important;
}

.auth-form-side .text-brand-500,
.auth-form-side a.font-medium {
  color: #6366f1 !important;
  font-weight: 600 !important;
  text-decoration: none !important;
}

.auth-form-side .text-brand-500:hover,
.auth-form-side a.font-medium:hover {
  color: #4f46e5 !important;
}

.auth-form-side [class$="__control"],
.auth-form-side [class*="__control "] {
  border-radius: 10px !important;
  min-height: 44px !important;
  border-color: #e2e8f0 !important;
}

.auth-form-side .z-social {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #1e293b;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.15s ease;
}
.auth-form-side .z-social:hover {
  border-color: #6366f1;
  background: #f8f7ff;
}

.auth-form-side .stack-y-6 > * + * { margin-top: 16px; }
.auth-form-side .stack-y-3 > * + * { margin-top: 12px; }

.auth-form-side input[type="checkbox"] {
  height: auto !important;
  width: auto !important;
  padding: 0 !important;
  border-radius: 4px !important;
}

.auth-form-side .auth-back-btn {
  height: 32px !important;
  font-size: 13px !important;
  padding: 0 8px !important;
  border: none !important;
  background: transparent !important;
  color: #64748b !important;
}

.auth-form-side .auth-back-btn:hover {
  color: #0f172a !important;
  background: #f1f5f9 !important;
}

@media (prefers-reduced-motion: reduce) {
  .auth-aurora, .auth-orb, .auth-badge-dot,
  .auth-stat-solo, .auth-sparkle,
  .auth-animate-in, .auth-gradient-text {
    animation: none !important;
  }
  .auth-animate-in { opacity: 1 !important; }
  .auth-stat-solo { opacity: 0 !important; }
  .auth-stat-solo-0 { opacity: 1 !important; }
}
`;

export default function AuthContainer(props: React.PropsWithChildren<Props>) {
  const variant = props.visualVariant || "login";

  useEffect(() => {
    const html = document.documentElement;
    const hadDark = html.classList.contains("dark");
    const prevColorScheme = html.style.colorScheme;
    html.classList.remove("dark");
    html.style.colorScheme = "light";
    const metas = document.querySelectorAll('meta[name="theme-color"]');
    const originals: (string | null)[] = [];
    metas.forEach((m) => {
      originals.push(m.getAttribute("content"));
      m.setAttribute("content", "#f9fafb");
    });
    return () => {
      if (hadDark) html.classList.add("dark");
      html.style.colorScheme = prevColorScheme;
      metas.forEach((m, i) => {
        const orig = originals[i];
        if (orig !== null) {
          m.setAttribute("content", orig);
        } else {
          m.removeAttribute("content");
        }
      });
    };
  }, []);

  return (
    <>
      <style suppressHydrationWarning dangerouslySetInnerHTML={{ __html: AUTH_STYLES }} />
      <div className="auth-page-wrapper">
        <VisualPanel variant={variant} />

        <div className="auth-form-side">
          <div className="auth-form-container">
            {props.backButton && (
              <div className="auth-animate-in" style={{ marginBottom: 8 }}>
                {props.backButton}
              </div>
            )}

            {props.showLogo && (
              <div className="auth-animate-in" style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
                <Logo inline={false} className="mx-auto" />
              </div>
            )}

            {props.heading && (
              <div className="auth-animate-in auth-delay-1" style={{ textAlign: "center", marginBottom: 24 }}>
                <h2 style={{ margin: 0, textTransform: "capitalize" }}>
                  {props.heading}
                </h2>
                {props.subheading && (
                  <p style={{ fontSize: 14, color: "#64748b", marginTop: 6, lineHeight: 1.4 }}>{props.subheading}</p>
                )}
              </div>
            )}

            {props.loading && (
              <div style={{ position: "absolute", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.8)" }}>
                <Loader />
              </div>
            )}

            <div className="auth-animate-in auth-delay-2">
              {props.children}
            </div>

            {props.footerText && (
              <div className="auth-animate-in auth-delay-3" style={{ textAlign: "center", marginTop: 24, paddingTop: 16, borderTop: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: 13, color: "#64748b" }}>
                  {props.footerText}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
